import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Waves, LogIn, UserPlus } from 'lucide-react';

const LOGO_URL = "/logo_surfskull.png";
const BEACH_URL = "https://customer-assets.emergentagent.com/job_rip-accounting-hub/artifacts/8vwronbm_Captura%20de%20pantalla%202026-04-08%20a%20las%2020.17.27.png";

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'caseta') return <Navigate to="/admin/crm" replace />;
    return <Navigate to="/monitor" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const result = await login(email, password);
    if (!result.success) setError(result.error);
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const result = await register(email, password, name);
    if (!result.success) setError(result.error);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FCFBF9]">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-3">
            <img src={LOGO_URL} alt="SURF SKULL" className="h-24 w-auto mx-auto rounded-xl" />
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>SURF ⚡ SKULL</h1>
            <p className="text-sm text-[#8A9BA8]">Sistema de contabilidad</p>
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 bg-[#F5F2ED]" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><LogIn className="w-4 h-4 mr-2" />Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><UserPlus className="w-4 h-4 mr-2" />Registro Monitor</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Email</Label>
                  <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" required className="border-[#E5E0D8]" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Contraseña</Label>
                  <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-[#E5E0D8]" />
                </div>
                {error && <p className="text-sm text-[#E07A5F]" data-testid="login-error">{error}</p>}
                <Button type="submit" data-testid="login-submit-button" disabled={isLoading} className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">{isLoading ? 'Entrando...' : 'Iniciar Sesión'}</Button>
              </form>
            </TabsContent>
            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nombre</Label>
                  <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="border-[#E5E0D8]" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Email</Label>
                  <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-[#E5E0D8]" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Contraseña</Label>
                  <Input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-[#E5E0D8]" />
                </div>
                {error && <p className="text-sm text-[#E07A5F]" data-testid="register-error">{error}</p>}
                <Button type="submit" data-testid="register-submit-button" disabled={isLoading} className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">{isLoading ? 'Registrando...' : 'Registrarse como Monitor'}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src={BEACH_URL} alt="Playa" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B3C]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-2 mb-3"><Waves className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">San Antolín · San Martín · Ría de Niembro</span></div>
          <p className="text-lg font-light opacity-90">Gestión integral de tu escuela de surf</p>
        </div>
      </div>
    </div>
  );
}
